import { useCallback, useRef, useState } from 'react';
import { cancelServiceRequest } from '../api/cancelServiceRequestApiService';
import {
  CANCEL_SERVICE_REQUEST_LABELS as L,
  UPDATE_SR_STATUS_LABELS,
} from '../constants/labels';
import { CANCEL_SERVICE_REQUEST_ERROR_MESSAGES } from '../errors/errorMessages';

/** Options accepted by {@link useCancelServiceRequest}. */
interface UseCancelServiceRequestOptions {
  /** Internal UUID of the service request to cancel (`ViewServiceRequestDetail.id`). */
  serviceRequestId: string | undefined;
  /** Called after a successful cancellation, before the modal closes. */
  onSuccess?: () => void;
  /** Called when the API returns 403 — modal is closed before this fires. */
  onPermissionDenied?: () => void;
}

/** Public interface returned by {@link useCancelServiceRequest}. */
export interface UseCancelServiceRequestReturn {
  /** Whether the cancel modal is currently open. */
  isOpen: boolean;
  /** Opens the modal and resets all form state. */
  open: () => void;
  /** Closes the modal (no-op while a submission is in flight). */
  close: () => void;
  /** Current value of the cancellation reason textarea. */
  reason: string;
  /** Updates `reason`; clears `reasonError` when the value becomes valid. */
  setReason: (value: string) => void;
  /** Inline validation error for the reason field. */
  reasonError: string;
  /** `true` while the API call is in flight. */
  isSubmitting: boolean;
  /** API-level error message shown below the form. */
  submitError: string;
  /** Clears `submitError`. */
  clearSubmitError: () => void;
  /** `true` when a submission can be initiated (`!isSubmitting`). */
  canSubmit: boolean;
  /** Validates, calls the API, and handles success/error state. */
  submit: () => Promise<void>;
}

/**
 * Manages state for the cancel service request flow.
 *
 * @remarks
 * Guards against double-submission via `isSubmittingRef` (a ref rather than a
 * state flag so closing the modal mid-flight does not trigger a re-render).
 * Validation runs on `submit`; the required-field message and character limit
 * are sourced from {@link UPDATE_SR_STATUS_LABELS} to avoid duplication.
 *
 * @param options - See {@link UseCancelServiceRequestOptions}.
 * @returns See {@link UseCancelServiceRequestReturn}.
 */
export const useCancelServiceRequest = ({
  serviceRequestId,
  onSuccess,
  onPermissionDenied,
}: UseCancelServiceRequestOptions): UseCancelServiceRequestReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReasonState] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [submitError, setSubmitError] = useState('');

  const resetForm = useCallback(() => {
    setReasonState('');
    setReasonError('');
    setSubmitError('');
  }, []);

  const open = useCallback(() => {
    resetForm();
    setIsOpen(true);
  }, [resetForm]);

  const close = useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }
    setIsOpen(false);
    resetForm();
  }, [resetForm]);

  const setReason = useCallback((value: string) => {
    setReasonState(value);
    if (
      value.trim().length > 0 &&
      value.trim().length <= UPDATE_SR_STATUS_LABELS.NOTES_MAX_LENGTH
    ) {
      setReasonError('');
    }
  }, []);

  const clearSubmitError = useCallback(() => setSubmitError(''), []);

  const canSubmit = !isSubmitting;

  const submit = useCallback(async () => {
    if (!serviceRequestId || isSubmittingRef.current) {
      return;
    }

    let hasError = false;
    if (!reason.trim()) {
      setReasonError(UPDATE_SR_STATUS_LABELS.ERROR_REQUIRED);
      hasError = true;
    } else if (
      reason.trim().length > UPDATE_SR_STATUS_LABELS.NOTES_MAX_LENGTH
    ) {
      setReasonError(L.ERROR_TOO_LONG);
      hasError = true;
    }
    if (hasError) {
      setSubmitError('');
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const result = await cancelServiceRequest({ serviceRequestId, reason });
      if (result.success) {
        setIsOpen(false);
        resetForm();
        onSuccess?.();
      } else {
        setSubmitError(
          result.error ?? CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.DEFAULT_ERROR
        );
        if (
          result.error === CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.UNAUTHORIZED
        ) {
          onPermissionDenied?.();
        }
      }
    } catch {
      setSubmitError(CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.UNEXPECTED_ERROR);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [serviceRequestId, reason, onSuccess, onPermissionDenied, resetForm]);

  return {
    isOpen,
    open,
    close,
    reason,
    setReason,
    reasonError,
    isSubmitting,
    submitError,
    clearSubmitError,
    canSubmit,
    submit,
  };
};
